import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./HomePage.css";
import mobileImage from "url:../../public/home.png";

function HomePage() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState([]);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRefs = useRef([]);

  useEffect(() => {
    setIsVisible(true);

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrolled / maxScroll) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);

    if (!isMobile) {
      document.body.style.backgroundImage = `url(${mobileImage})`;
      document.body.style.backgroundSize = "cover";
      document.body.style.backgroundRepeat = "no-repeat";
      document.body.style.backgroundAttachment = "fixed";
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
      document.body.style.backgroundImage = null;
      document.body.style.backgroundSize = null;
      document.body.style.backgroundRepeat = null;
      document.body.style.backgroundAttachment = null;
    };
  }, [isMobile]);

  useEffect(() => {
    const observers = [];

    sectionRefs.current.forEach((section, index) => {
      if (!section) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                setVisibleSections((prev) => [...new Set([...prev, index])]);
              }, index * 200);
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.3,
          rootMargin: "0px",
        },
      );

      observer.observe(section);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  const sections = [
    {
      icon: "🏢",
      year: "2007",
      title: "Our Foundation",
      text: `Established in 2007 by Charbel Matta, a Lebanese certified public
      accountant and sworn court expert, MATTA Financial Services has
      evolved into a distinguished audit and consulting firm. Over the years,
      strategic growth initiatives have seen the addition of new partners
      and managers, including US Certified Public Accountants, enriching
      our team with diverse expertise.`,
    },
    {
      icon: "🌍",
      year: "Today",
      title: "Our Reach",
      text: `At MATTA Financial Services, we take pride in our commitment to 
      excellence and client-centric approach. Currently, we cater to a 
      robust customer portfolio, with over 60 active domestic companies  
      located throughout Lebanon. Additionally, our reach extends globally, 
      serving more than 5 active international enterprises.`,
    },
    {
      icon: "🎯",
      year: "Future",
      title: "Our Vision",
      text: `Our journey is marked by a relentless pursuit of professional integrity, 
      innovation, and tailored solutions. As we continue to expand our 
      horizons, MATTA Financial Services remains dedicated to providing 
      unparalleled audit and consulting services, contributing to the 
      success of businesses both locally and on the international stage.`,
    },
  ];

  const stats = [
    { value: "16+", label: "Years of Excellence" },
    { value: "60+", label: "Active Clients" },
    { value: "5+", label: "International Clients" },
    { value: "100%", label: "Client Satisfaction" },
  ];

  return (
    <div className={`home-container ${isVisible ? "visible" : ""}`}>
      <div
        className="scroll-progress"
        style={{ width: `${scrollProgress}%` }}
      ></div>

      <div className="hero-section">
        {isMobile && (
          <div className="image-container">
            <img src={mobileImage} alt="Background" className="mobile-image" />
            <div className="image-overlay">
              <h1 className="overlay-title">MATTA Financial Services</h1>
            </div>
          </div>
        )}

        <div className="hero-content">
          <h1 className="main-title">
            Welcome to <span className="highlight">MATTA</span> Financial
            Services
          </h1>
          <p className="tagline">Precision in numbers, excellence in advice.</p>
        </div>
      </div>

      <div className="stats-section">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="stat-card"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="timeline-container">
        {sections.map((section, index) => (
          <div
            key={index}
            ref={(el) => (sectionRefs.current[index] = el)}
            className={`timeline-item ${visibleSections.includes(index) ? "visible" : ""} ${
              index % 2 === 0 ? "left" : "right"
            }`}
          >
            <div className="timeline-marker">
              <div className="marker-icon">{section.icon}</div>
              <div className="marker-year">{section.year}</div>
            </div>
            <div className="timeline-content">
              <h2 className="section-title">{section.title}</h2>
              <p className="section-text">{section.text}</p>
              <div className="section-decoration"></div>
            </div>
          </div>
        ))}
        <div className="timeline-line"></div>
      </div>

      <div className="cta-section">
        <h2 className="cta-title">Ready to Get Started?</h2>
        <p className="cta-subtitle">
          Let's discuss how we can help your business grow
        </p>
        <div className="cta-buttons">
          <button
            className="cta-btn primary"
            onClick={() => navigate("/our-services")}
          >
            Explore Our Services →
          </button>
          <button
            className="cta-btn secondary"
            onClick={() => navigate("/contact-information")}
          >
            Contact Us
          </button>
        </div>
      </div>

      <div className="floating-elements">
        <div className="float-element element-1"></div>
        <div className="float-element element-2"></div>
        <div className="float-element element-3"></div>
      </div>
    </div>
  );
}

export default HomePage;
